import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { type ErroApi } from '@/nucleo/auth/cliente-api';
import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

/**
 * A camada de dados da moderação de OCR de documentos.
 *
 * Mesmo desenho das outras telas de dados (`operacao/comum/dados.ts`,
 * `admin/integracoes/dados.ts`): nenhum componente chama `chamarApi` direto, e a
 * chave de cache mora aqui, junto de quem a usa.
 *
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
 * `/documentos/ocr/pendentes` ainda não existe na API — não há módulo de
 * documentos/OCR em `apps/api/src/modulos/`. Decisão do dono de 2026-08-26
 * (CLAUDE.md, "Proibido", exceção estendida) para demonstração ao cliente —
 * mesmas três condições dos cinco módulos de cadastro: só leitura, dado
 * claramente fictício, removido antes de `develop` → `hml` (`git grep -n
 * "TEMPORÁRIO" apps/web/src`, checklist em `docs/ALVOS.md`). "Aprovar" e
 * "Reprovar" continuam sem gravar nada de verdade (`useModerarExtracao` abaixo).
 *
 * PRÉ-VISUALIZAÇÃO SEM SUPABASE STORAGE: o protótipo buscava a URL pública no
 * Storage do Supabase (`supabase.storage.from('documentos').getPublicUrl(...)`).
 * Este sistema não tem (ainda) integração de armazenamento de arquivo nenhuma —
 * o mock deixa `arquivo_url` nulo de propósito, e a tela cai no mesmo aviso de
 * "pré-visualização indisponível" que o protótipo já mostrava: isso é um estado
 * real da interface, não uma lacuna do mock.
 */

export const CHAVES_DE_MODERACAO_OCR = {
  pendentes: ['documentos', 'ocr-pendentes'] as const,
};

export type DecisaoDeModeracao = 'APROVADO' | 'REPROVADO';

/** O credenciado dono do documento, no recorte mínimo que esta tela usa. */
export interface CredenciadoDoDocumentoOcr {
  nome: string | null;
  /** Não usado para link — a tela de detalhe do credenciado ainda não existe. */
  slug: string | null;
}

/** O documento original por trás de uma extração de OCR. */
export interface DocumentoOcr {
  tipo: string | null;
  arquivo_nome: string | null;
  /**
   * URL já pronta para `src` de `<img>`/`<iframe>`, se a API tiver uma. Nula ou
   * ausente vira o aviso de pré-visualização indisponível — nunca uma chamada a
   * um serviço de armazenamento.
   */
  arquivo_url: string | null;
  credenciado: CredenciadoDoDocumentoOcr | null;
}

/** Um item da fila de moderação: o documento e o que o OCR leu dele. */
export interface ExtracaoOcr {
  id: string;
  confianca: number | null;
  campos_extraidos: Record<string, unknown> | null;
  texto_bruto: string | null;
  created_at: string;
  documento: DocumentoOcr | null;
}

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
// Três documentos em três estágios de confiança (alta, média, baixa), sem
// `arquivo_url` (ver o doc-comment do topo) e um deles sem `credenciado` para
// exercitar o traço "—" que a tela já mostra para esse caso.
const EXTRACOES_OCR_MOCK: ExtracaoOcr[] = [
  {
    id: 'ocr-exemplo-01',
    confianca: 0.96,
    campos_extraidos: {
      numero_cnh: '00000000001',
      categoria: 'D',
      validade: '2028-04-12',
      nome_condutor: 'Condutor Exemplo 1',
    },
    texto_bruto: null,
    created_at: '2026-08-25T13:40:00Z',
    documento: {
      tipo: 'CNH do condutor',
      arquivo_nome: 'cnh-condutor-exemplo-1.pdf',
      arquivo_url: null,
      credenciado: { nome: 'Transportadora Exemplo 1 Ltda', slug: 'transportadora-exemplo-1' },
    },
  },
  {
    id: 'ocr-exemplo-02',
    confianca: 0.71,
    campos_extraidos: {
      placa: 'ABC1D23',
      renavam: '00000000000',
      validade_crlv: '2027-01-31',
    },
    texto_bruto: 'CRLV-e ... PLACA ABC1D23 ... RENAVAM 00000000000 ... VALIDADE 01/2027',
    created_at: '2026-08-25T15:12:00Z',
    documento: {
      tipo: 'CRLV do veículo',
      arquivo_nome: 'crlv-veiculo-exemplo.pdf',
      arquivo_url: null,
      credenciado: { nome: 'Transportadora Exemplo 2 Ltda', slug: 'transportadora-exemplo-2' },
    },
  },
  {
    id: 'ocr-exemplo-03',
    confianca: 0.38,
    campos_extraidos: {},
    texto_bruto:
      'Documento digitalizado com baixa legibilidade — nenhum campo reconhecido com segurança.',
    created_at: '2026-08-26T08:05:00Z',
    documento: {
      tipo: 'Comprovante de curso obrigatório',
      arquivo_nome: 'curso-obrigatorio-exemplo.jpg',
      arquivo_url: null,
      credenciado: null,
    },
  },
];

/** A fila de extrações que aguardam revisão humana. */
export function useExtracoesPendentes(): UseQueryResult<ExtracaoOcr[], ErroApi> {
  return useQuery<ExtracaoOcr[], ErroApi>({
    queryKey: CHAVES_DE_MODERACAO_OCR.pendentes,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => EXTRACOES_OCR_MOCK,
    retry: naoInsistirEmRecusa,
  });
}

export interface ParametrosDeModeracao {
  id: string;
  decisao: DecisaoDeModeracao;
  motivo?: string;
}

/**
 * "Aprovar"/"Reprovar" não gravam nada — não há rota de moderação na API, e
 * fingir que persistiu seria o oposto do que CLAUDE.md pede ("Regras de
 * interface", item 8; ver `comum/cadastroIndisponivel.ts`). O motivo da reprovação
 * continua sendo validado de verdade na tela (campo obrigatório); só o passo final
 * troca de "gravar" para "avisar com honestidade".
 */
export function useModerarExtracao(): { moderar: (parametros: ParametrosDeModeracao) => void } {
  return {
    moderar: () => {
      mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);
    },
  };
}
